import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { membersKeys } from "@/features/members/queries/membersQueryKeys";
import { sessionsKeys } from "@/features/sessions/queries/sessionsQueryKeys";
import bookingService, {
    type AdminCreateAttendanceInput,
    type AdminCreateAttendanceResult,
} from "@/features/sessions/services/bookingService";

type Variables = AdminCreateAttendanceInput;

export function useAdminCreateAttendance() {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (variables: Variables): Promise<AdminCreateAttendanceResult> => {
            return await bookingService.adminCreateAttendance(variables);
        },
        onError: (error) => {
            const message = error instanceof Error ? error.message : "Failed to book member. Please try again.";
            toast.error(message);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: sessionsKeys.attendance.session(variables.sessionId) });
            queryClient.invalidateQueries({ queryKey: sessionsKeys.sessions.detail(variables.sessionId) });
            queryClient.invalidateQueries({ queryKey: membersKeys.detail(variables.targetUid) });
            queryClient.invalidateQueries({ queryKey: membersKeys.all });
            toast.success("Booking saved");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
